import { UserLanguage } from '../../features/account/types/User'
import { getMarketIsosByUserLanguage } from '../../features/markets/utils/utils'

// An analyst-curated example screenshot on a market archetype (a Market Level
// Blueprint in the UI, a Blueprint Family once the rename lands), linked to the live
// event it was taken from. The same DTO serves all four internal archetype endpoints
// as `glossaryImages`.
//
// Only `url` and `eventId` are stored. Event screenshots have no ids of their own
// (they are a plain `string[]` on the event), so `url` is both the image and the
// reference to which screenshot was picked. Everything else is resolved from the
// live event on every read, so it follows the event if it is moved to another game
// or its type is reassigned.
//
// An entry the BE can no longer resolve — its event deleted, or `url` no longer
// among the event's screenshots — is omitted from every response, and removed from
// storage at the moment the screenshot goes. So an `Image` that arrives is always
// whole; there is no partially resolved entry to flag.
export type BlueprintFamilyExampleImage = {
  // The picked screenshot. Always one of the source event's own `screenshotUrls`.
  url: string
  eventId: string
  // Resolved from the event: needed with `eventId` to deep-link into the event
  // dialog (`m_trackedGameId` / `m_liveEventTypeId` / `m_liveEventId`).
  gameId: string
  eventTypeId: string
  eventName: string
  // Position of `url` within the event's *current* screenshots, so a reader
  // clicking through lands on this exact shot rather than the event's first one.
  screenshotIndex: number
  // Market code → game name (`us`, `jp`, …). The one resolved field that can be
  // absent, for a game with no names on record; the BE serialises it as missing
  // rather than null.
  gameNames?: { [marketIso: string]: string }
}

// The two fields a write carries per entry. The rest of a read entry is derived by the
// BE and has no place in a request.
export type BlueprintFamilyExampleImageInput = Pick<BlueprintFamilyExampleImage, 'url' | 'eventId'>

export const toBlueprintFamilyExampleImageInput = ({ url, eventId }: BlueprintFamilyExampleImage): BlueprintFamilyExampleImageInput => ({ url, eventId })

// Enforced by the BE with a 400; the editor stops the analyst one step earlier.
export const BLUEPRINT_FAMILY_MAX_EXAMPLE_IMAGES = 10

// The game's name in the reader's language, by the same market preference the
// `Game` model uses for its own `resolvedName`, falling back to whatever name the
// game has. Undefined only when the BE sent no names at all.
export const resolveExampleImageGameName = (image: Pick<BlueprintFamilyExampleImage, 'gameNames'>, userLanguage: UserLanguage): string | undefined => {
  const names = image.gameNames
  if (!names) return undefined
  const preferred = getMarketIsosByUserLanguage(userLanguage).find((marketIso) => Boolean(names[marketIso]))
  return preferred ? names[preferred] : Object.values(names).find(Boolean)
}

// Two entries are the same screenshot only when they point at the same shot *on the
// same event*: the BE handles a screenshot shared by several events per event, so
// the picker must not treat it as already added just because the url matches. The
// same `{url, eventId}` pair twice in one list is a 400, which is what this keeps the
// analyst away from.
export const isSameExampleImage = (a: BlueprintFamilyExampleImageInput, b: BlueprintFamilyExampleImageInput): boolean =>
  a.eventId === b.eventId && a.url === b.url
